import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActionsPopup, Button, IconButton, Modal, Switch } from 'src/components';
import { Mem, PictureMetadata } from 'src/api/learningSessions/types';
import { MemsApi } from 'src/api';
import { sharedMemMessages } from '../sharedMemMessages';
import {
  applyMarkupToSelection,
  BOLD_MARKER,
  DEFAULT_PICTURE_METADATA,
  hasMarkupFormat,
  ITALIC_MARKER,
} from '../memTextFormat';
import { MemMarkdown } from '../MemMarkdown';
import { useMemVote } from './useMemVote';
import * as S from './styles';

const MAX_MEM_LENGTH = 280;

// Voting is hidden for now but kept in place (see useMemVote) so it can be
// switched back on later.
const SHOW_MEM_VOTES: boolean = false;

const msgs = defineMessages({
  upvote: { id: 'mem_card_upvote', defaultMessage: 'Upvote' },
  downvote: { id: 'mem_card_downvote', defaultMessage: 'Downvote' },
  report: { id: 'mem_card_report', defaultMessage: 'Report' },
  edit: { id: 'mem_card_edit', defaultMessage: 'Edit' },
  delete: { id: 'mem_card_delete', defaultMessage: 'Delete' },
  deleteConfirmHeader: {
    id: 'mem_delete_confirm_header',
    defaultMessage: 'Are you sure you want to delete your mem?',
  },
  deleteConfirmText: {
    id: 'mem_delete_confirm_text',
    defaultMessage: 'This action cannot be undone.',
  },
  deleteConfirm: { id: 'mem_delete_confirm_action', defaultMessage: 'Delete' },
  editTitle: { id: 'mem_edit_modal_title', defaultMessage: 'Edit mem' },
  save: { id: 'mem_form_save', defaultMessage: 'Save' },
});

export interface MemCardProps {
  mem: Mem;
  currentUserId: number | null;
  onDeleted: (memId: number) => void;
  onEdited: (mem: Mem) => void;
  initialAiPending?: boolean;
  /** Shows the mem text in full, for the "all mems" modal. */
  isExpanded?: boolean;
}

export const MemCard: FC<MemCardProps> = ({
  mem,
  currentUserId,
  onDeleted,
  onEdited,
  initialAiPending = false,
  isExpanded = false,
}) => {
  const intl = useIntl();
  const { voteCount, userVote, isVoting, upvote, downvote } = useMemVote(mem.id, mem.voteCount);
  const [isReported, setIsReported] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(mem.imageUrl);
  const [isAiPending, setIsAiPending] = useState(initialAiPending);

  const pictureMeta = mem.pictureMetadata ?? DEFAULT_PICTURE_METADATA;

  const editTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const editPendingSelectionRef = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    if (editPendingSelectionRef.current !== null && editTextAreaRef.current) {
      editTextAreaRef.current.setSelectionRange(
        editPendingSelectionRef.current.start,
        editPendingSelectionRef.current.end,
      );
      editPendingSelectionRef.current = null;
    }
  });

  const [editText, setEditText] = useState(mem.text ?? '');
  const [editPictureMeta, setEditPictureMeta] = useState<PictureMetadata | null>(
    mem.pictureMetadata,
  );
  const [editIsSensitive, setEditIsSensitive] = useState(mem.isSensitive);
  const [imageAspectRatio, setImageAspectRatio] = useState<string | null>(null);

  const isOwner = currentUserId !== null && mem.creatorId === currentUserId;
  const displayImageUrl = localImageUrl;
  const hasImage = Boolean(displayImageUrl);

  // Calculate aspect ratio for natural image display
  useEffect(() => {
    if (displayImageUrl) {
      const img = new window.Image();
      img.onload = () => {
        const aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
        setImageAspectRatio(aspectRatio);
      };
      img.onerror = () => {
        setImageAspectRatio(null); // Fallback to fixed dimensions
      };
      img.src = displayImageUrl;
    } else {
      setImageAspectRatio(null);
    }
  }, [displayImageUrl]);

  useEffect(() => {
    if (!isAiPending) return;
    const intervalId = setInterval(async () => {
      try {
        const result = await MemsApi.pollAiImageStatus(mem.id);
        if (result.status === 'completed' && result.imageUrl) {
          setLocalImageUrl(result.imageUrl);
          setIsAiPending(false);
        } else if (result.status === 'failed') {
          setIsAiPending(false);
        }
      } catch {
        // silently ignore poll errors
      }
    }, 3000);
    return () => clearInterval(intervalId);
  }, [isAiPending, mem.id]);

  const handleReport = async () => {
    if (isReported) return;
    try {
      await MemsApi.reportMem(mem.id);
      setIsReported(true);
    } catch {
      // silently fail
    }
  };

  const handleDelete = async () => {
    try {
      await MemsApi.deleteMem(mem.id);
      onDeleted(mem.id);
    } catch {
      // silently fail
    }
  };

  const handleEdit = async () => {
    if (isSubmitting || editText.trim().length === 0 || editText.length > MAX_MEM_LENGTH) return;
    setIsSubmitting(true);
    try {
      const updated = await MemsApi.updateMem(mem.id, {
        text: editText,
        isSensitive: editIsSensitive,
        ...(editPictureMeta !== null && { pictureMetadata: editPictureMeta }),
      });
      onEdited(updated);
      setIsEditModalOpen(false);
    } catch {
      // silently fail
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyEditMarkup = (marker: string) => {
    const textarea = editTextAreaRef.current;
    const selectionStart = textarea?.selectionStart ?? 0;
    const selectionEnd = textarea?.selectionEnd ?? 0;
    const {
      text: newText,
      newSelectionStart,
      newSelectionEnd,
    } = applyMarkupToSelection(editText, marker, selectionStart, selectionEnd);
    setEditText(newText);
    editPendingSelectionRef.current = { start: newSelectionStart, end: newSelectionEnd };
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isCmd = e.metaKey; // Mac Command key
    const isCtrl = e.ctrlKey; // Windows/Linux Ctrl key

    if ((isCmd || isCtrl) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      applyEditMarkup(BOLD_MARKER);
    } else if ((isCmd || isCtrl) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      applyEditMarkup(ITALIC_MARKER);
    }
  };

  const openEditModal = () => {
    setEditText(mem.text ?? '');
    setEditPictureMeta(mem.pictureMetadata ? { ...mem.pictureMetadata } : null);
    setEditIsSensitive(mem.isSensitive);
    setIsEditModalOpen(true);
  };

  return (
    <>
      <S.Card data-testid="mem-card" $hasImage={hasImage}>
        {isAiPending && !displayImageUrl ? (
          <S.ImageSkeleton data-testid="mem-image-skeleton" />
        ) : (
          hasImage && (
            <S.ImageBox
              $imageUrl={displayImageUrl ?? undefined}
              $imageX={pictureMeta.imageX}
              $imageY={pictureMeta.imageY}
              $aspectRatio={imageAspectRatio}
              data-testid="mem-image"
            />
          )
        )}
        <S.Content $hasImage={hasImage}>
          {!(isAiPending && !displayImageUrl) && (
            <S.MemText
              $hasImage={hasImage}
              $align={pictureMeta.textAlign}
              $fontSize={pictureMeta.fontSize}
              $isExpanded={isExpanded}
              data-testid="mem-text"
            >
              <MemMarkdown text={mem.text} />
            </S.MemText>
          )}
          <S.ActionsRow>
            {SHOW_MEM_VOTES && (
              <S.VoteGroup>
                <S.ActionButton
                  onClick={upvote}
                  disabled={isVoting}
                  $active={userVote === 'up'}
                  aria-label={intl.formatMessage(msgs.upvote)}
                  aria-pressed={userVote === 'up'}
                  data-testid="mem-upvote-button"
                >
                  ▲
                </S.ActionButton>
                <S.VoteCount data-testid="mem-vote-count">{voteCount}</S.VoteCount>
                <S.ActionButton
                  onClick={downvote}
                  disabled={isVoting}
                  $active={userVote === 'down'}
                  aria-label={intl.formatMessage(msgs.downvote)}
                  aria-pressed={userVote === 'down'}
                  data-testid="mem-downvote-button"
                >
                  ▼
                </S.ActionButton>
              </S.VoteGroup>
            )}

            {!isOwner && (
              <S.ActionButton
                onClick={handleReport}
                disabled={isReported}
                aria-label={intl.formatMessage(msgs.report)}
                data-testid="mem-report-button"
              >
                🚩
              </S.ActionButton>
            )}

            {isOwner && (
              <>
                <IconButton
                  handleClick={openEditModal}
                  ariaLabel={intl.formatMessage(msgs.edit)}
                  icon="edit"
                />
                <IconButton
                  handleClick={() => setIsDeleteModalOpen(true)}
                  ariaLabel={intl.formatMessage(msgs.delete)}
                  icon="deleteIcon"
                />
              </>
            )}
          </S.ActionsRow>
        </S.Content>
      </S.Card>

      <ActionsPopup
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        header={intl.formatMessage(msgs.deleteConfirmHeader)}
        text={intl.formatMessage(msgs.deleteConfirmText)}
        primaryBtnText={intl.formatMessage(msgs.deleteConfirm)}
        primaryBtnAction={handleDelete}
        secondaryBtnText={intl.formatMessage(sharedMemMessages.cancel)}
        secondaryBtnAction={() => setIsDeleteModalOpen(false)}
      />

      <Modal
        isOpen={isEditModalOpen}
        handleClose={() => setIsEditModalOpen(false)}
        shouldCloseOnOutsideClick
        shouldCloseOnEsc
        darkCloseIcon
      >
        <S.EditModal>
          <S.ModalTitle>{intl.formatMessage(msgs.editTitle)}</S.ModalTitle>
          <S.FormatToolbar>
            <S.FormatButton
              type="button"
              $active={hasMarkupFormat(editText, BOLD_MARKER)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyEditMarkup(BOLD_MARKER)}
              data-testid="mem-edit-bold"
            >
              <strong>{intl.formatMessage(sharedMemMessages.bold)}</strong>
            </S.FormatButton>
            <S.FormatButton
              type="button"
              $active={hasMarkupFormat(editText, ITALIC_MARKER)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyEditMarkup(ITALIC_MARKER)}
              data-testid="mem-edit-italic"
            >
              <em>{intl.formatMessage(sharedMemMessages.italic)}</em>
            </S.FormatButton>
          </S.FormatToolbar>
          <S.TextArea
            ref={editTextAreaRef}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onKeyDown={handleEditKeyDown}
            data-testid="mem-edit-textarea"
          />
          {editText && (
            <S.TextPreview data-testid="mem-edit-text-preview">
              <MemMarkdown text={editText} />
            </S.TextPreview>
          )}
          <S.CharCount $overLimit={editText.length > MAX_MEM_LENGTH}>
            {editText.length}/{MAX_MEM_LENGTH}
          </S.CharCount>

          {/* Image positioning controls - only show if mem has an image */}
          {hasImage && editPictureMeta && (
            <S.ImageSection>
              <S.ImageSectionTitle>Image & Text Position</S.ImageSectionTitle>
              <S.ImageRow>
                <S.ImagePreviewCard
                  $imageUrl={displayImageUrl ?? undefined}
                  $imageX={editPictureMeta.imageX}
                  $imageY={editPictureMeta.imageY}
                  $aspectRatio={imageAspectRatio}
                  data-testid="mem-edit-image-preview"
                >
                  {editText && (
                    <S.ImagePreviewText
                      $align={editPictureMeta.textAlign}
                      $position={editPictureMeta.textPosition}
                      $fontSize={editPictureMeta.fontSize}
                    >
                      <MemMarkdown text={editText} />
                    </S.ImagePreviewText>
                  )}
                </S.ImagePreviewCard>
                <S.VerticalButtons>
                  {(['top', 'middle', 'bottom'] as const).map(p => (
                    <S.FormatButton
                      key={p}
                      type="button"
                      $active={editPictureMeta.textPosition === p}
                      onClick={() =>
                        setEditPictureMeta(prev => ({
                          ...(prev || DEFAULT_PICTURE_METADATA),
                          textPosition:
                            prev?.textPosition === p ? DEFAULT_PICTURE_METADATA.textPosition : p,
                        }))
                      }
                      data-testid={`mem-edit-position-${p}`}
                    >
                      {p === 'top' ? '↑' : p === 'middle' ? '↕' : '↓'}
                    </S.FormatButton>
                  ))}
                </S.VerticalButtons>
              </S.ImageRow>
              <S.HorizontalControls>
                <S.ControlGroup>
                  {(['left', 'center', 'right'] as const).map(a => (
                    <S.FormatButton
                      key={a}
                      type="button"
                      $active={editPictureMeta.textAlign === a}
                      onClick={() =>
                        setEditPictureMeta(prev => ({
                          ...(prev || DEFAULT_PICTURE_METADATA),
                          textAlign: prev?.textAlign === a ? DEFAULT_PICTURE_METADATA.textAlign : a,
                        }))
                      }
                      data-testid={`mem-edit-align-${a}`}
                    >
                      {a === 'left' ? '←' : a === 'center' ? '↔' : '→'}
                    </S.FormatButton>
                  ))}
                </S.ControlGroup>
              </S.HorizontalControls>
            </S.ImageSection>
          )}
          <S.SwitchRow>
            <span>{intl.formatMessage(sharedMemMessages.sensitiveLabel)}</span>
            <Switch
              checked={editIsSensitive}
              onChange={e => setEditIsSensitive((e.target as HTMLInputElement).checked)}
              data-testid="mem-edit-sensitive-switch"
            />
          </S.SwitchRow>
          <S.ModalButtons>
            <Button
              buttonType="secondary"
              buttonSize="medium"
              onClick={() => setIsEditModalOpen(false)}
              data-testid="mem-edit-cancel-button"
            >
              {intl.formatMessage(sharedMemMessages.cancel)}
            </Button>
            <Button
              buttonType="primary"
              buttonSize="medium"
              onClick={handleEdit}
              disabled={
                isSubmitting || editText.trim().length === 0 || editText.length > MAX_MEM_LENGTH
              }
              loading={isSubmitting}
              data-testid="mem-edit-save-button"
            >
              {intl.formatMessage(msgs.save)}
            </Button>
          </S.ModalButtons>
        </S.EditModal>
      </Modal>
    </>
  );
};
